import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { maskMoneyInput, parseInputToApi, formatDate, moneyToInput } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';
import { CategorySelect } from '@/features/categories/CategorySelect';
import type { Contract } from './types';
import { ADJUSTMENT_INDEXES, FREQUENCIES } from './types';

/**
 * Cadastro de contrato — RF-CON-01.
 *
 * O formulário só mostra o que a configuração escolhida usa: sem renovação
 * automática não aparece prazo de renovação, sem reajuste não aparece
 * periodicidade. Campos que o sistema vai ignorar convidam a preencher e
 * produzem um contrato diferente do que o usuário acha que configurou.
 */
export function ContractForm({
  contract,
  selectedIds,
  onClose,
  onSaved,
}: {
  contract: Contract | null;
  selectedIds: number[];
  onClose: () => void;
  /** Recebe a mensagem do servidor sobre o que foi gerado, quando houver. */
  onSaved: (note?: string) => void;
}) {
  const [title, setTitle] = useState(contract?.title ?? '');
  const [number, setNumber] = useState(contract?.number ?? '');
  const [direction, setDirection] = useState(contract?.direction ?? 'receita');
  const [status, setStatus] = useState(contract?.status ?? 'ativo');
  const [entityId, setEntityId] = useState(contract?.entity_id?.toString() ?? '');
  const [partyId, setPartyId] = useState(contract?.primary_party_id?.toString() ?? '');
  const [categoryId, setCategoryId] = useState(contract?.category_id?.toString() ?? '');

  const [startsAt, setStartsAt] = useState(contract?.starts_at ?? new Date().toISOString().slice(0, 10));
  const [endsAt, setEndsAt] = useState(contract?.ends_at ?? '');
  const [autoRenew, setAutoRenew] = useState(contract?.auto_renew ?? false);
  const [renewalMonths, setRenewalMonths] = useState(String(contract?.renewal_months ?? 12));
  const [noticeDays, setNoticeDays] = useState(String(contract?.notice_days ?? 30));

  const [amount, setAmount] = useState(
    contract ? moneyToInput(contract.amount) : '',
  );
  const [frequency, setFrequency] = useState(contract?.frequency ?? 'mensal');
  const [dueDay, setDueDay] = useState(String(contract?.due_day ?? 1));
  // Mantido no payload para não zerar o que já estiver gravado; não é mais
  // editável porque deixou de governar o horizonte de geração.
  const advanceDays = String(contract?.billing_advance_days ?? 0);

  const [adjustmentIndex, setAdjustmentIndex] = useState(contract?.adjustment_index ?? 'nenhum');
  const [adjustmentPercentage, setAdjustmentPercentage] = useState(
    contract?.adjustment_percentage?.toString() ?? '',
  );
  const [adjustmentMonths, setAdjustmentMonths] = useState(String(contract?.adjustment_months ?? 12));

  /**
   * De onde o faturamento começa — só na criação.
   *
   * `inicio` gera também o que ficou para trás, inclusive vencidos. `hoje`
   * ignora o passado. As duas são legítimas e o sistema não tem como adivinhar
   * qual é o caso: contrato antigo cujas parcelas já foram recebidas precisa
   * dos títulos para dar baixa; contrato antigo que só passa a ser controlado
   * agora não deveria nascer com meses de atraso fictício.
   */
  const [billFrom, setBillFrom] = useState<'inicio' | 'hoje'>('inicio');
  const [error, setError] = useState<string | null>(null);

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60_000,
  });

  // Contatos vêm de `/parties`: o endpoint de referência não os traz, e a lista
  // pode ser longa demais para entrar num payload carregado em toda tela.
  const partyList = useQuery({
    queryKey: ['parties', 'options', selectedIds],
    queryFn: () =>
      api.get<{ data: Array<{ id: number; name: string }> }>('/parties?per_page=200', {
        entityIds: selectedIds,
      }),
    select: (r) => r.data,
    staleTime: 5 * 60_000,
  });

  const hasTerm = endsAt !== '';
  const startsInPast = startsAt !== '' && startsAt < new Date().toISOString().slice(0, 10);
  const adjusts = adjustmentIndex !== 'nenhum';
  const indexInfo = ADJUSTMENT_INDEXES.find((i) => i.id === adjustmentIndex);

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        entity_id: Number(entityId),
        title: title.trim(),
        number: number.trim() || null,
        direction,
        status,
        starts_at: startsAt,
        ends_at: endsAt || null,
        // Prazo indeterminado não renova: não há o que renovar.
        auto_renew: hasTerm ? autoRenew : false,
        renewal_months: hasTerm && autoRenew ? Number(renewalMonths) : null,
        notice_days: hasTerm ? Number(noticeDays) : null,
        amount: parseInputToApi(amount),
        currency: 'BRL',
        frequency,
        due_day: frequency === 'unica' ? null : Number(dueDay),
        billing_advance_days: Number(advanceDays),
        adjustment_index: adjustmentIndex,
        adjustment_percentage: adjustmentIndex === 'fixo' ? Number(adjustmentPercentage) : null,
        adjustment_months: adjusts ? Number(adjustmentMonths) : null,
        category_id: categoryId === '' ? null : Number(categoryId),
      };

      if (!contract) {
        payload.first_billing_at = billFrom === 'hoje' ? new Date().toISOString().slice(0, 10) : null;
        payload.parties = partyId
          ? [{ party_id: Number(partyId), role: direction === 'receita' ? 'contratante' : 'contratada', is_primary: true }]
          : [];
      }

      return contract
        ? api.put<{ meta?: { note?: string } }>(`/contracts/${contract.id}`, payload, {
            entityIds: selectedIds,
          })
        : api.post<{ meta?: { note?: string } }>('/contracts', payload, { entityIds: selectedIds });
    },
    onSuccess: (res) => onSaved(res.meta?.note),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Não foi possível salvar o contrato.'),
  });

  const entities = reference.data?.entities ?? [];
  const parties = partyList.data ?? [];
  /**
   * Só folhas, como em todo o resto do sistema.
   *
   * Aqui faltava o `selectable`: a lista trazia também as categorias-pai, que
   * servem de agrupador e não de destino. Lançar em "Receita Operacional" em
   * vez de "Contratos recorrentes" perde a granularidade que faz o relatório
   * por categoria valer alguma coisa — e o formulário de contrato era o único
   * que oferecia esse caminho.
   */
  const categories = (reference.data?.categories ?? []).filter(
    (c) => c.selectable && c.type === direction,
  );

  return (
    <Modal title={contract ? 'Editar contrato' : 'Novo contrato'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
        className="space-y-3.5"
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo" required>
            <select
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value as 'receita' | 'despesa');
                setCategoryId('');
              }}
              className="input w-full"
            >
              <option value="receita">Eu recebo</option>
              <option value="despesa">Eu pago</option>
            </select>
          </Field>

          <Field label="Situação">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="input w-full">
              <option value="ativo">Ativo — já fatura</option>
              <option value="rascunho">Rascunho — não fatura</option>
              <option value="suspenso">Suspenso — não fatura</option>
            </select>
          </Field>
        </div>

        <Field label="Título" required hint="Ex.: Consultoria mensal PUAH">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={250}
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Número do contrato">
            <input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              maxLength={60}
              placeholder="2026/014"
              className="input w-full"
            />
          </Field>

          <Field label="Entidade" required>
            <select
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              required
              className="input w-full"
            >
              <option value="">Selecione</option>
              {entities.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={direction === 'receita' ? 'Cliente' : 'Fornecedor'}>
            <select value={partyId} onChange={(e) => setPartyId(e.target.value)} className="input w-full" disabled={!!contract}>
              <option value="">Nenhum</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Categoria">
            <CategorySelect
              value={categoryId}
              onChange={setCategoryId}
              type={direction}
              categories={categories}
              emptyLabel="Nenhuma"
            />
          </Field>
        </div>

        {/* --- Valor e faturamento --- */}
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Valor e faturamento
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Valor" required>
              <input
                value={amount}
                onChange={(e) => setAmount(maskMoneyInput(e.target.value))}
                inputMode="decimal"
                placeholder="R$ 0,00"
                required
                className="input w-full"
              />
            </Field>

            <Field label="Periodicidade" required>
              <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="input w-full">
                {FREQUENCIES.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* --- De onde começa a faturar (só na criação) --- */}
          {!contract && startsInPast && (
            <div className="mt-3 rounded-lg bg-amber-50 p-3 dark:bg-amber-950/30">
              <p className="text-xs font-medium text-amber-900 dark:text-amber-200">
                A vigência começa em {formatDate(startsAt)}, que já passou. Gerar os títulos desde
                quando?
              </p>

              <div className="mt-2 space-y-1.5">
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    checked={billFrom === 'inicio'}
                    onChange={() => setBillFrom('inicio')}
                    className="mt-0.5 border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
                  />
                  <span>
                    Desde o início da vigência
                    <span className="block text-xs text-amber-800 dark:text-amber-300">
                      Gera também os meses passados — eles nascem <strong>vencidos</strong>. Use
                      quando essas cobranças existem de verdade e você precisa dar baixa nelas.
                    </span>
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    checked={billFrom === 'hoje'}
                    onChange={() => setBillFrom('hoje')}
                    className="mt-0.5 border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
                  />
                  <span>
                    A partir de hoje
                    <span className="block text-xs text-amber-800 dark:text-amber-300">
                      Ignora o passado. Use quando o contrato é antigo e você só quer controlar
                      daqui para frente.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {frequency !== 'unica' && (
            <div className="mt-3">
              <Field label="Dia do vencimento" hint="Dia 31 cai no último dia do mês.">
                <input
                  type="number"
                  value={dueDay}
                  onChange={(e) => setDueDay(e.target.value)}
                  min={1}
                  max={31}
                  className="input w-full"
                />
              </Field>

              {/* Sem campo de antecedência: o contrato gera 12 meses à frente,
                  igual às recorrências. Um campo em dias, ao lado disso, seria
                  um controle que não controla nada. */}
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Os títulos são gerados <strong>12 meses à frente</strong> (ou até o fim da vigência,
                o que vier primeiro). Os futuros entram como <strong>previstos</strong> — aparecem
                em A receber e na previsão de caixa, sem contar como vencidos.
              </p>
            </div>
          )}
        </div>

        {/* --- Vigência --- */}
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Vigência
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Início" required>
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                required
                className="input w-full"
              />
            </Field>

            <Field label="Término" hint="Em branco = prazo indeterminado.">
              <input
                type="date"
                value={endsAt}
                min={startsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="input w-full"
              />
            </Field>
          </div>

          {/* Sem prazo não há vencimento, e portanto não há renovação nem aviso. */}
          {hasTerm && (
            <>
              <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoRenew}
                  onChange={(e) => setAutoRenew(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
                />
                Renova automaticamente
              </label>

              <div className="mt-3 grid grid-cols-2 gap-3">
                {autoRenew && (
                  <Field label="Renova por (meses)" required>
                    <input
                      type="number"
                      value={renewalMonths}
                      onChange={(e) => setRenewalMonths(e.target.value)}
                      min={1}
                      max={600}
                      required
                      className="input w-full"
                    />
                  </Field>
                )}

                <Field
                  label="Avisar com (dias)"
                  hint={
                    autoRenew
                      ? 'Prazo para avisar que NÃO quer renovar.'
                      : 'Antecedência do aviso de vencimento.'
                  }
                >
                  <input
                    type="number"
                    value={noticeDays}
                    onChange={(e) => setNoticeDays(e.target.value)}
                    min={0}
                    max={365}
                    className="input w-full"
                  />
                </Field>
              </div>
            </>
          )}
        </div>

        {/* --- Reajuste --- */}
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Reajuste
          </p>

          <Field label="Índice">
            <select
              value={adjustmentIndex}
              onChange={(e) => setAdjustmentIndex(e.target.value)}
              className="input w-full"
            >
              {ADJUSTMENT_INDEXES.map((i) => (
                <option key={i.id} value={i.id}>{i.label}</option>
              ))}
            </select>
          </Field>

          {indexInfo && (
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">{indexInfo.hint}</p>
          )}

          {adjusts && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              {adjustmentIndex === 'fixo' && (
                <Field label="Percentual (%)" required>
                  <input
                    type="number"
                    value={adjustmentPercentage}
                    onChange={(e) => setAdjustmentPercentage(e.target.value)}
                    step="0.01"
                    min={-100}
                    max={1000}
                    required
                    className="input w-full"
                  />
                </Field>
              )}

              <Field label="A cada (meses)" required>
                <input
                  type="number"
                  value={adjustmentMonths}
                  onChange={(e) => setAdjustmentMonths(e.target.value)}
                  min={1}
                  max={120}
                  required
                  className="input w-full"
                />
              </Field>
            </div>
          )}
        </div>

        {error && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={save.isPending || title.trim() === '' || entityId === '' || amount === ''}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
